import {
  formatDateToIsoStringTime,
  formatDateToIstIsoString,
  formatDateToMomentUtc,
  getFileFormat,
} from '@/utils/helper'

import { StoreData } from '../types'

export interface PujaMedia {
  media_type: string
  media_url: string
  media_description: string
  media_thumbnail: string
}

export interface ScheduleData {
  id: string
  day: number
  start_time: string | null
  start_media: PujaMedia[] | null
}

export interface TempleData {
  temple_id: string
  temple_name: string
  schedules: ScheduleData[]
}

export interface PujaUpdateForm {
  // Legacy fields for single temple/single day
  puja_start_time?: string
  puja_end_time?: string
  puja_media?: PujaMedia[]
  priest_medias?: PujaMedia[]

  // New multi-temple multi-day fields
  multi_day?: boolean
  multi_temple?: boolean
  num_of_days?: number
  interval?: string
  start_date?: string
  temples_data?: TempleData[]

  // Common fields
  new_puja_state:
    | 'STARTED'
    | 'COMPLETED'
    | 'SCHEDULED'
    | 'BOOKED'
    | 'CANCELLED'
    | 'RESCHEDULED'
  message_cards: { header: string; description: string }[]
  scheduled_at: string
  init_scheduled_time: string
  change_status_immediately: boolean
}

export function parseStoreResponse(storeResponseData: StoreData) {
  const baseResponse: any = {
    ...storeResponseData,
    puja_start_time: storeResponseData?.puja_start_time
      ? formatDateToMomentUtc(storeResponseData?.puja_start_time)
      : undefined,
    puja_end_time: storeResponseData?.puja_end_time
      ? formatDateToMomentUtc(storeResponseData?.puja_end_time)
      : undefined,
    init_scheduled_time:
      storeResponseData?.is_scheduled === true &&
      storeResponseData?.init_scheduled_time
        ? formatDateToMomentUtc(
            storeResponseData?.init_scheduled_time,
            '+05:30',
          )
        : undefined,
    puja_media:
      storeResponseData?.puja_media?.map((media: any) => ({
        ...media,
        media_url: media?.media_url ? [media?.media_url] : undefined,
        media_thumbnail: media?.media_thumbnail
          ? [media?.media_thumbnail]
          : undefined,
      })) || [],
    priest_medias:
      storeResponseData?.priest_medias?.map((media: any) => ({
        ...media,
        media_url: media?.media_url ? [media?.media_url] : undefined,
        media_thumbnail: media?.media_thumbnail
          ? [media?.media_thumbnail]
          : undefined,
      })) || [],
    message_cards:
      storeResponseData?.message_cards?.map((media: any) => ({
        ...media,
        cta_media: {
          ...media?.cta_media,
          media_url:
            media?.cta_media?.media_type === 'url'
              ? media?.cta_media?.media_url
              : [media?.cta_media?.media_url],
          media_type: media?.cta_media?.media_type,
          media_thumbnail: media?.cta_media?.media_thumbnail
            ? [media?.media_thumbnail]
            : undefined,
        },
      })) || [],
  }

  // Handle multi-temple multi-day data if present, otherwise set defaults
  if ((storeResponseData as any)?.temples_data) {
    // Existing multi-temple multi-day data
    baseResponse.multi_temple =
      (storeResponseData as any)?.multi_temple || false
    baseResponse.multi_day = (storeResponseData as any)?.multi_day || false
    baseResponse.num_of_days = (storeResponseData as any)?.num_of_days || 1
    baseResponse.interval = (storeResponseData as any)?.interval || 'day'

    // Parse start_date if present
    if ((storeResponseData as any)?.start_date) {
      baseResponse.start_date = formatDateToMomentUtc(
        (storeResponseData as any).start_date,
        '+05:30',
      )
    }

    baseResponse.temples_data = (storeResponseData as any).temples_data.map(
      (temple: any) => ({
        ...temple,
        schedules:
          temple?.schedules?.map((schedule: any) => ({
            ...schedule,
            id: schedule?.id,
            start_time: schedule.start_time
              ? formatDateToMomentUtc(schedule.start_time, '+05:30')
              : null,
            start_media:
              schedule.start_media?.map((media: any) => ({
                ...media,
                media_url: media?.media_url ? [media?.media_url] : undefined,
                media_thumbnail: media?.media_thumbnail
                  ? [media?.media_thumbnail]
                  : undefined,
              })) || [],
          })) || [],
      }),
    )
  } else {
    // Initialize default values for new multi-temple multi-day structure
    baseResponse.multi_temple = false
    baseResponse.multi_day = false
    baseResponse.num_of_days = 1
    baseResponse.interval = 'day'
    baseResponse.start_date = undefined
  }

  return baseResponse
}

export function preparePayload(data: PujaUpdateForm) {
  const basePayload: any = {
    puja_start_time: data?.puja_start_time
      ? formatDateToIsoStringTime(data?.puja_start_time)
      : null,
    puja_end_time: data?.puja_end_time
      ? formatDateToIsoStringTime(data?.puja_end_time)
      : null,
    ...(!data?.change_status_immediately && {
      scheduled_at: data?.init_scheduled_time
        ? formatDateToIstIsoString(data?.init_scheduled_time)
        : null,
    }),
    is_scheduled:
      data?.multi_temple || data?.multi_day
        ? false
        : !data?.change_status_immediately,
    puja_media:
      data?.puja_media && data?.puja_media?.length > 0
        ? data?.puja_media?.map((item: any) => {
            const fileUrl = item?.media_url?.[0]
            const fileExtension = fileUrl?.split?.('.')?.pop?.()
            const mediaType = getFileFormat(fileExtension)
            return {
              ...item,
              media_type: mediaType,
              media_url: fileUrl,
              media_thumbnail: item?.media_thumbnail?.[0],
            }
          })
        : null,
    new_puja_state: data?.new_puja_state,
    message_cards:
      data?.message_cards?.length > 0
        ? data?.message_cards?.map((item: any) => {
            let fileUrl, fileExtension, mediaType
            if (item?.cta_media?.mediaType !== 'url') {
              fileUrl = item?.cta_media?.media_url?.[0]
              fileExtension = fileUrl?.split?.('.')?.pop?.()
              mediaType = getFileFormat(fileExtension)
            }
            return {
              ...item,
              cta_media: {
                ...item?.cta_media,
                media_type:
                  item?.cta_media?.media_type === 'url' ? 'url' : mediaType,
                media_url:
                  item?.cta_media?.media_type === 'url'
                    ? item?.cta_media?.media_url
                    : item?.cta_media?.media_url?.[0],
                media_thumbnail: item?.cta_media?.media_thumbnail?.[0],
              },
            }
          })
        : null,
  }

  // Handle multi-temple multi-day structure
  if (data?.multi_temple || data?.multi_day) {
    basePayload.multi_day = data.multi_day || false
    basePayload.multi_temple = data.multi_temple || false
    basePayload.num_of_days = data.num_of_days || 1
    basePayload.interval = data.interval || 'day'
    basePayload.start_date = data.start_date
      ? formatDateToIstIsoString(data.start_date)
      : null

    if (data?.temples_data && data.temples_data.length > 0) {
      basePayload.temples_data = data?.temples_data?.map(
        (temple: TempleData) => ({
          temple_id: temple?.temple_id || null,
          temple_name: temple?.temple_name,
          schedules:
            temple?.schedules?.map((schedule: ScheduleData) => ({
              id: schedule?.id || null,
              day: schedule?.day,
              start_time: schedule?.start_time
                ? formatDateToIstIsoString(schedule?.start_time)
                : null,
              start_media:
                schedule.start_media && schedule.start_media.length > 0
                  ? schedule.start_media.map((media: any) => {
                      const fileUrl = media?.media_url?.[0]
                      const fileExtension = fileUrl?.split?.('.')?.pop?.()
                      const mediaType = getFileFormat(fileExtension)
                      return {
                        ...media,
                        media_type: mediaType,
                        media_url: fileUrl, // Convert array back to string
                        media_thumbnail: media?.media_thumbnail?.[0], // Convert array back to string
                      }
                    })
                  : null,
            })) || [],
        }),
      )
    }
  } else {
    // Legacy single temple single day structure
    basePayload.puja_start_time = data?.puja_start_time
      ? formatDateToIsoStringTime(data?.puja_start_time)
      : null
    basePayload.puja_end_time = data?.puja_end_time
      ? formatDateToIsoStringTime(data?.puja_end_time)
      : null
    basePayload.puja_media =
      data?.puja_media && data?.puja_media?.length > 0
        ? data?.puja_media?.map((item: any) => {
            const fileUrl = item?.media_url?.[0]
            const fileExtension = fileUrl?.split?.('.')?.pop?.()
            const mediaType = getFileFormat(fileExtension)
            return {
              ...item,
              media_type: mediaType,
              media_url: fileUrl,
              media_thumbnail: item?.media_thumbnail?.[0],
            }
          })
        : null
    basePayload.priest_medias =
      data?.priest_medias && data?.priest_medias?.length > 0
        ? data?.priest_medias?.map((item: any) => {
            const fileUrl = item?.media_url?.[0]
            const fileExtension = fileUrl?.split?.('.')?.pop?.()
            const mediaType = getFileFormat(fileExtension)
            return {
              ...item,
              media_type: mediaType,
              media_url: fileUrl,
              media_thumbnail: item?.media_thumbnail?.[0],
            }
          })
        : null
  }

  return basePayload
}
